/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * TK-32 mục A (22/10/2026) — KHUNG "VẤN ĐỀ" DÙNG CHUNG: một chỗ cho mọi cảnh báo, chia TAB theo mức và
 * lọc theo NGUỒN (tủ / đèn NEMA), có ô tìm. Thiết kế: `_BUS/thiet_ke/TK-31_32/HopVanDe.dc.html` (bản 2).
 *
 * Nguồn dữ liệu = ĐÚNG cái đang có, không thêm bảng:
 *   · `GET /api/alarms?active=true[&cabinetId]` — hồ sơ ĐANG MỞ (mức qua `phanMuc`, mất kết nối qua `laMatKetNoi`)
 *   · `GET /api/alarms?active=false&daDongNgay=7`  — "Đã xử lý" 7 ngày
 *   · `vanDeGiaLap` (prop, chỉ khi `pham = cabinetId`) — vấn đề từ dữ liệu GIẢ LẬP (`NemaIssue`), nhãn GIẢ LẬP,
 *     KHÔNG bao giờ ở phạm vi hệ thống (chốt mốc 3)
 *   · `chuaDoc` (prop) — việc "chưa đọc" (ELR, lớp bảo vệ, sức khoẻ): KHÔNG phải cảnh báo, không lên chuông
 * Hành động Ghi nhận / Đã xử lý gọi cùng API `POST /api/alarms/:id/ack` · `/xu-ly` như Tổng quan.
 *
 * Quy tắc giao diện (lee 22/10): một dòng = chấm mức · tiêu đề · nơi · giờ · nút. Không câu diễn giải.
 * TK-32 B (dev_app 22/10): Tổng quan, ngăn kéo chuông và `/van-de` cùng dùng khung này. Tổng quan truyền `veDong`
 *   để mỗi hồ sơ vẽ bằng `DongVanDe` sẵn có (Ghi nhận · Đã xử lý kèm ghi chú · ba mốc · đồng bộ lịch hai chiều) —
 *   khung lo tab/chip/tìm, dòng giữ nguyên hành vi cũ.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '../lib/api';
import { qk, useAlarms, useAlarmsDaDong, useHoatDongCoNghia, useTree } from '../lib/queries';
import { AlertOctagon, AlertTriangle, CheckCircle2, Lightbulb, MapPin, RadioTower, WifiOff } from 'lucide-react';
import { dongHoatDong, HOAT_DONG_CO_NGHIA, tenViec } from '../lib/loi_nguoi';
import { aiDaLam } from '../lib/nguonthaotac';
import { DauDangKiem, HuyHieuKhoa } from './Khoa';
import { cauDoTuoi, phanMuc } from '../lib/canhbao';
import { fmtTime } from '../lib/format';
import { demTheoNhom, dungDong, LOC_TAB, soSuCoMo, type DemSuCo, type Dong, type DongChuaDoc, type Muc as Muc_, type NguonVanDe, type TabVanDe, type VanDeGiaLapVao } from '../lib/vande';
import { CoQuyen, useAuth } from '../lib/auth';
import { Badge, Button, Spinner, useToast } from './ui';
import type { Alarm } from '../lib/types';

export type { TabVanDe, NguonVanDe, DongChuaDoc, VanDeGiaLapVao } from '../lib/vande';

type Muc = Muc_;
const CHAM: Record<Muc | 'chuadoc' | 'xong', string> = { khan: 'bg-nguy', canh: 'bg-canh', mat: 'bg-chu-3', chuadoc: 'bg-chu-3/60', xong: 'bg-tot' };
const TEN_MUC: Record<Muc, string> = { khan: 'Khẩn', canh: 'Cảnh báo', mat: 'Mất kết nối' };

/**
 * TK-32 B.1 — hai câu trước sống ở dải vàng (chỉ cho hồ sơ ĐẦU), nay đi theo TỪNG hồ sơ trong khung:
 *   · số đo đã cũ ⇒ nói ra (YC-44, `cauDoTuoi`) — hàng còn mở nhưng lần đo cuối đã lâu, không khẳng định như vừa đo;
 *   · mã lẽ ra CHƯA CÓ NGUỒN mà vẫn hiện ⇒ nói ra (báo dev).
 */
function PhuChuHoSo({ a }: { a: Alarm }) {
  const tuoi = cauDoTuoi(a.doTuoi, (ms) => fmtTime(ms));
  const chuaNguon = phanMuc(a.code, a.mucDo).chuaCoNguon;
  if (!tuoi && !chuaNguon) return null;
  return (
    <p className="m-0 text-xs text-chu-3" data-o="phu-chu-ho-so">
      {tuoi}
      {chuaNguon && <span className="ml-1">(mã này lẽ ra CHƯA CÓ NGUỒN — báo dev)</span>}
    </p>
  );
}

/**
 * TK-33 (22/10) — MỘT NGUỒN cho mọi số "vấn đề" của phạm vi hệ thống: khung này, ô số và vòng tròn Tổng quan đều gọi
 *   ĐÚNG hook này (cùng hai truy vấn, cùng `dungDong`). Không chỗ nào tự đếm lại `alarms`.
 */
export function useDongHeThong(): { dong: Dong[]; dangTai: boolean; loi: boolean } {
  const mo = useAlarms();
  const xong = useAlarmsDaDong(7);
  const dong = useMemo(() => dungDong({ mo: mo.data, xong: xong.data, heThong: true }), [mo.data, xong.data]);
  return { dong, dangTai: mo.isLoading, loi: mo.isError };
}

/**
 * TK-33b — SỐ ĐẾM sự cố đang mở toàn hệ (`GET /api/alarms/dem`), không phụ thuộc trần 100 hàng của danh sách. Khoá nằm DƯỚI
 *   `qk.alarms()` ⇒ mọi chỗ làm mới danh sách (WebSocket, Ghi nhận, Đã xử lý) làm mới luôn số đếm. `null` = chưa có (luật số 0).
 */
export function useDemSuCo(): DemSuCo | null {
  const q = useQuery({
    queryKey: [...qk.alarms(), 'dem'] as const,
    queryFn: () => api.get<{ mo: number; theoMa: { code: string; mucDo: number | null; n: number }[] }>('/api/alarms/dem'),
    refetchInterval: 30_000,
  });
  return useMemo(() => (q.data ? demTheoNhom(q.data.theoMa) : null), [q.data]);
}

/** Tuổi ngắn "N ngày / N giờ / N phút" — dòng gọn của Tổng quan (TK-33). */
const tuoi = (iso: string | null | undefined, bayGio: number) => {
  if (!iso) return '';
  const s = Math.max(0, Math.floor((bayGio - new Date(iso).getTime()) / 1000));
  if (s >= 86400) return `${Math.floor(s / 86400)} ngày`;
  if (s >= 3600) return `${Math.floor(s / 3600)} giờ`;
  return `${Math.max(1, Math.floor(s / 60))} phút`;
};
const SO_DONG_GON = 6;

const gioNgan = (iso: string | null | undefined) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/**
 * Hai lệnh của MỘT hồ sơ — Ghi nhận (`/ack`) · Đã xử lý (`/xu-ly`). TK-33b: tách ra để khung đầy đủ và Trung tâm sự cố
 * dùng CÙNG một bản (không chép hai lần cùng một lời gọi). "Đã xử lý" với mã máy chủ tự đóng được ⇒ hồ sơ chờ tủ xác nhận.
 */
export function useHanhDongVanDe() {
  const qc = useQueryClient();
  const toast = useToast();
  const lamMoi = () => {
    void qc.invalidateQueries({ queryKey: qk.alarms() }); void qc.invalidateQueries({ queryKey: qk.overview });
    void qc.invalidateQueries({ queryKey: ['audit'] });
  };
  const ghiNhan = useMutation({
    mutationFn: (id: string) => api.post(`/api/alarms/${id}/ack`, {}),
    onSuccess: () => { toast.success('Đã ghi nhận'); lamMoi(); },
    onError: (e) => toast.error('Không ghi nhận được', errorMessage(e)),
  });
  const xuLy = useMutation({
    mutationFn: (id: string) => api.post<{ ketQua?: 'dong-ngay' | 'cho-tu-xac-nhan' }>(`/api/alarms/${id}/xu-ly`, {}),
    onSuccess: (kq) => {
      toast.success(kq?.ketQua === 'cho-tu-xac-nhan' ? 'Đã ghi tên bạn — hồ sơ tự đóng khi tủ báo bình thường lại' : 'Đã đánh dấu xử lý');
      lamMoi();
    },
    onError: (e) => toast.error('Không đánh dấu được', errorMessage(e)),
  });
  return { ghiNhan, xuLy, ban: ghiNhan.isPending || xuLy.isPending };
}

export function HopVanDe({
  pham, tenDen, vanDeGiaLap, chuaDoc, tabMacDinh = 'mo', nguonMacDinh = 'tat', tieuDe = 'Vấn đề', compact = false,
  veDong, hanhDong, chanDanhSach, gon = false,
}: {
  /**
   * TK-33 — bản GỌN cho cột phải Tổng quan: tab viên thuốc, không ô tìm / chip nguồn, mỗi dòng chấm · tiêu đề ·
   *   "Tủ · giờ" · tuổi; tối đa 6 dòng, còn lại "Xem tất cả" (/van-de). Không nút hành động — hành động ở /van-de.
   */
  gon?: boolean;
  /** Dòng cuối VÙNG CUỘN (Tổng quan: "Còn N hồ sơ nữa chưa tải") — nằm cùng chỗ với thứ nó nói về. */
  chanDanhSach?: ReactNode;
  /** Vẽ một hồ sơ ĐANG MỞ bằng dòng riêng của chỗ gọi (Tổng quan: `DongVanDe`). Vắng ⇒ dòng mặc định. */
  veDong?: (a: Alarm) => ReactNode;
  /** Chip/nút đặt cạnh tiêu đề (Tổng quan: "Đang mở · x / y hồ sơ"). */
  hanhDong?: ReactNode;
  /** 'he-thong' hoặc cabinetId */
  pham: 'he-thong' | string;
  /** tên đèn theo addr (tab NEMA truyền vào để dòng nói "Trụ 04" thay vì mã) */
  tenDen?: (addr: number) => string;
  vanDeGiaLap?: VanDeGiaLapVao[];
  chuaDoc?: DongChuaDoc[];
  tabMacDinh?: TabVanDe;
  nguonMacDinh?: NguonVanDe;
  tieuDe?: string;
  /** trong tab tủ: bỏ lọc tuyến, ô tìm ngắn */
  compact?: boolean;
}) {
  const heThong = pham === 'he-thong';
  const cabinetId = heThong ? undefined : pham;
  const mo = useAlarms(cabinetId);
  const xong = useAlarmsDaDong(7);
  const [tab, setTab] = useState<TabVanDe>(tabMacDinh);
  const [nguon, setNguon] = useState<NguonVanDe>(nguonMacDinh);
  const [tim, setTim] = useState('');

  const { ghiNhan, xuLy } = useHanhDongVanDe();

  const tatCa = useMemo<Dong[]>(
    () => dungDong({ mo: mo.data, xong: xong.data, vanDeGiaLap, chuaDoc, heThong, cabinetId, tenDen }),
    [mo.data, xong.data, vanDeGiaLap, chuaDoc, heThong, cabinetId, tenDen],
  );

  const theoNguon = (d: Dong) => nguon === 'tat' || d.nguon === nguon;
  const theoTim = (d: Dong) => !tim.trim() || (d.tieu + ' ' + d.noi).toLowerCase().includes(tim.trim().toLowerCase());
  const loc = LOC_TAB;

  const TABS: { k: TabVanDe; ten: string }[] = [
    { k: 'mo', ten: 'Đang mở' }, { k: 'khan', ten: 'Khẩn' }, { k: 'canh', ten: 'Cảnh báo' }, { k: 'mat', ten: 'Mất kết nối' },
    ...(chuaDoc && chuaDoc.length ? [{ k: 'chuadoc' as const, ten: 'Chưa đọc' }] : []),
    { k: 'xong', ten: 'Đã xử lý' },
  ];
  const dem = (k: TabVanDe) => tatCa.filter(loc[k]).filter(theoNguon).length;
  const rows = tatCa.filter(loc[tab]).filter(theoNguon).filter(theoTim);
  const dangTai = mo.isLoading || (tab === 'xong' && xong.isLoading);
  const ban = ghiNhan.isPending || xuLy.isPending;

  if (gon) {
    const bayGio = Date.now();
    const hien = rows.slice(0, SO_DONG_GON);
    return (
      <section className="card flex min-w-0 flex-col overflow-hidden" data-o="hop-van-de" data-pham="he-thong" data-gon>
        <div className="flex items-center gap-2 px-4 pt-3">
          <h2 className="chu-tieude m-0 text-sm font-semibold text-chu-1">{tieuDe}</h2>
          <Link to="/van-de" className="ml-auto text-xs font-semibold text-nhan hover:underline">Xem tất cả →</Link>
        </div>
        <div role="tablist" aria-label="Mức" className="flex flex-wrap gap-1.5 px-4 pb-2 pt-2.5">
          {TABS.filter((t) => t.k !== 'chuadoc').map((t) => {
            const n = dem(t.k); const on = t.k === tab;
            return (
              <button key={t.k} type="button" role="tab" aria-selected={on} data-tab={t.k} onClick={() => setTab(t.k)}
                className={`inline-flex min-h-[30px] items-center gap-1 whitespace-nowrap rounded-full border px-2.5 text-xs font-semibold ${on ? 'border-nhan bg-nhan/15 text-chu-1' : 'border-vien text-chu-2 hover:text-chu-1'}`}>
                {t.ten}
                {t.k !== 'xong' && <span className={`tabular-nums ${t.k === 'khan' && n > 0 ? 'text-nguy' : ''}`} data-so-tab>{n}</span>}
              </button>
            );
          })}
        </div>
        <div className="min-h-0 flex-1">
          {dangTai && <div className="p-4"><Spinner label="Đang tải…" /></div>}
          {!dangTai && rows.length === 0 && (
            <div className="border-t border-vien px-4 py-6 text-center" data-khong-viec>
              {tab === 'mo' && <CheckCircle2 className="mx-auto mb-1.5 h-6 w-6 text-nhan" aria-hidden="true" />}
              <p className="m-0 text-sm text-chu-3">{tab === 'mo' ? 'Không có vấn đề nào đang mở' : 'Không có'}</p>
            </div>
          )}
          {hien.map((d) => (
            <Link key={d.id} to={d.cabinetId ? `/tu/${d.cabinetId}${d.nguon === 'nema' ? '?tab=nema' : ''}` : '/van-de'}
              className="flex items-start gap-2.5 border-t border-vien px-4 py-2 hover:bg-nen-2/40" data-dong={d.id} data-muc={d.muc} data-nguon={d.nguon}>
              <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${CHAM[d.muc]}`} role="img" aria-label={d.muc === 'xong' ? 'Đã xử lý' : d.muc === 'chuadoc' ? 'Chưa đọc' : TEN_MUC[d.muc]} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-chu-1">{d.tieu}</span>
                <span className="block truncate text-xs text-chu-3">{[d.alarm?.cabinet?.name, gioNgan(d.luc)].filter(Boolean).join(' · ')}</span>
              </span>
              {d.luc && <span className="shrink-0 text-xs tabular-nums text-chu-3">{tuoi(d.luc, bayGio)}</span>}
            </Link>
          ))}
          {rows.length > SO_DONG_GON && (
            <Link to="/van-de" className="block border-t border-vien px-4 py-2 text-xs font-semibold text-nhan hover:underline" data-o="con-lai">
              Xem tất cả · còn {rows.length - SO_DONG_GON}
            </Link>
          )}
          {chanDanhSach && <div className="border-t border-vien px-4 py-2 text-xs text-chu-3" data-o="chan-danh-sach">{chanDanhSach}</div>}
        </div>
      </section>
    );
  }

  return (
    <section className="card overflow-hidden" data-o="hop-van-de" data-pham={heThong ? 'he-thong' : 'tu'}>
      <div className="flex flex-wrap items-center gap-2 px-4 pt-3">
        <h2 className="m-0 text-base font-semibold text-chu-1">{tieuDe}</h2>
        {hanhDong}
        <div className="ml-auto flex items-center gap-2">
          <label htmlFor={`hv-tim-${pham}`} className="sr-only">Tìm</label>
          <input id={`hv-tim-${pham}`} type="search" value={tim} onChange={(e) => setTim(e.target.value)} placeholder="Tìm"
            className={`min-h-[36px] rounded-lg border border-vien bg-nen-lom px-3 text-sm text-chu-1 placeholder:text-chu-3 ${compact ? 'w-36' : 'w-48'}`} />
        </div>
      </div>
      <div role="tablist" aria-label="Mức" className="flex gap-0.5 overflow-x-auto border-b border-vien px-2 pt-1">
        {TABS.map((t) => {
          const n = dem(t.k); const on = t.k === tab;
          return (
            <button key={t.k} type="button" role="tab" aria-selected={on} data-tab={t.k} onClick={() => setTab(t.k)}
              className={`-mb-px inline-flex min-h-[40px] items-center gap-1.5 whitespace-nowrap border-b-2 px-3 text-[13px] font-semibold ${on ? 'border-nhan text-chu-1' : 'border-transparent text-chu-2 hover:text-chu-1'}`}>
              {t.ten}
              <span className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-bold ${t.k === 'khan' && n > 0 ? 'bg-nguy-nen text-nguy' : 'bg-nen-2 text-chu-2'}`}>{n}</span>
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-2 px-4 py-2.5">
        {([['tat', 'Tất cả'], ['tu', 'Tủ'], ['nema', 'Đèn']] as [NguonVanDe, string][]).map(([k, ten]) => (
          <button key={k} type="button" aria-pressed={nguon === k} onClick={() => setNguon(k)}
            className={`min-h-[32px] rounded-full border px-3 text-xs font-medium ${nguon === k ? 'border-nhan bg-nhan/15 text-chu-1' : 'border-vien text-chu-2 hover:text-chu-1'}`}>{ten}</button>
        ))}
      </div>
      <div className="max-h-[520px] overflow-y-auto">
        {dangTai && <div className="p-4"><Spinner label="Đang tải…" /></div>}
        {/* Rỗng: HÌNH + câu, màu `nhan` — KHÔNG màu lá: "không có hồ sơ nào mở" không nói hệ đang chạy đúng ([95]). */}
        {!dangTai && rows.length === 0 && (
          <div className="border-t border-vien px-4 py-6 text-center" data-khong-viec>
            {tab === 'mo' && <CheckCircle2 className="mx-auto mb-1.5 h-6 w-6 text-nhan" aria-hidden="true" />}
            <p className="m-0 text-sm text-chu-3">{tab === 'mo' ? 'Không có vấn đề nào đang mở' : 'Không có'}</p>
          </div>
        )}
        {rows.map((d) => (d.alarm && d.muc !== 'xong' && veDong) ? (
          <div key={d.id} className="border-t border-vien px-3 pb-1" data-dong={d.id} data-muc={d.muc} data-nguon={d.nguon}>{veDong(d.alarm)}<PhuChuHoSo a={d.alarm} /></div>
        ) : (
          <div key={d.id} className="flex items-center gap-3 border-t border-vien px-4 py-2.5 hover:bg-nen-2/40" data-dong={d.id} data-muc={d.muc} data-nguon={d.nguon} data-gia-lap={d.giaLap ? '1' : undefined}>
            <span className={`h-2 w-2 shrink-0 rounded-full ${CHAM[d.muc]}`} role="img" aria-label={d.muc === 'xong' ? 'Đã xử lý' : d.muc === 'chuadoc' ? 'Chưa đọc' : TEN_MUC[d.muc]} />
            <div className="min-w-0 flex-1">
              <p className="m-0 truncate text-sm font-semibold text-chu-1">
                {d.tieu}
                {d.giaLap && <Badge tone="slate" className="ml-2">GIẢ LẬP</Badge>}
              </p>
              {d.noi && <p className="m-0 truncate text-xs text-chu-3">{d.noi}</p>}
              {d.alarm && d.muc !== 'xong' && <PhuChuHoSo a={d.alarm} />}
            </div>
            {d.luc && <span className="shrink-0 text-xs tabular-nums text-chu-3">{gioNgan(d.luc)}</span>}
            {d.muc === 'chuadoc' && d.onDoc && <Button size="sm" variant="secondary" onClick={d.onDoc}>Đọc</Button>}
            {d.alarm && d.muc !== 'xong' && !d.daNhan && (
              <CoQuyen khoa="nhan_canh_bao" an>
                <Button size="sm" variant="secondary" disabled={ban} onClick={() => ghiNhan.mutate(d.alarm!.id)}>Ghi nhận</Button>
              </CoQuyen>
            )}
            {d.alarm && d.muc !== 'xong' && d.daNhan && !d.alarm.resolvedAt && (
              <CoQuyen khoa="nhan_canh_bao" an>
                <Button size="sm" variant="secondary" disabled={ban} onClick={() => xuLy.mutate(d.alarm!.id)}>Đã xử lý</Button>
              </CoQuyen>
            )}
            {d.alarm && d.muc !== 'xong' && d.alarm.resolvedAt && <span className="text-xs text-chu-3">chờ tủ</span>}
            {heThong && d.cabinetId && <Link to={`/tu/${d.cabinetId}${d.nguon === 'nema' ? '?tab=nema' : ''}`} className="text-xs font-semibold text-nhan hover:underline">Xem</Link>}
          </div>
        ))}
        {chanDanhSach && <div className="border-t border-vien px-4 py-2 text-xs text-chu-3" data-o="chan-danh-sach">{chanDanhSach}</div>}
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════════
 * TK-33b (lee 22/10 chiều) — TRUNG TÂM SỰ CỐ = `HopVanDe` MẶC ÁO MỚI (mẫu chạy được `SDKD_Smart_City_Lighting.html`).
 *   CÙNG dòng (`useDongHeThong` → `dungDong`), CÙNG bộ lọc (`LOC_TAB`), CÙNG hai lệnh (`useHanhDongVanDe`) — không nguồn thứ hai.
 *   Tab Đang mở NN · Đã xử lý NN; chip Tất cả · Khẩn cấp · Cảnh báo · Mất kết nối (mẫu không có chip cuối — Kapp tách nhóm này);
 *   thẻ vạch màu trái (đỏ khẩn · vàng cảnh báo · XÁM mất kết nối); Định vị + Tiếp nhận (= Ghi nhận) / Hoàn tất (= Đã xử lý);
 *   dưới: Hoạt động gần đây (nhật ký, khoá khi không có quyền).
 * ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════════ */

type LocMuc = 'tat' | 'khan' | 'canh' | 'mat';
const VACH: Record<Muc | 'chuadoc' | 'xong', string> = { khan: 'border-nguy', canh: 'border-canh', mat: 'border-chu-3', chuadoc: 'border-chu-3', xong: 'border-tot' };
const hai = (n: number) => String(n).padStart(2, '0');
const SO_HOAT_DONG_TT = 5;
const LOP_HD = { xam: 'bg-chu-3', xanh: 'bg-tot', vang: 'bg-canh' } as const;

export function TrungTamSuCo({ onDinhVi, className = '' }: {
  /** Bay tới tủ + mở bảng tủ (trang chính). Vắng ⇒ nút "Định vị" mở trang chính với tủ ấy. */
  onDinhVi?: (cabinetId: string) => void;
  className?: string;
}) {
  const { dong, dangTai } = useDongHeThong();
  const dem = useDemSuCo();
  const { ghiNhan, xuLy, ban } = useHanhDongVanDe();
  const navigate = useNavigate();
  const { coQuyen, daBietQuyen } = useAuth();
  const coNhatKy = coQuyen('tao_tai_khoan');
  const hd = useHoatDongCoNghia(HOAT_DONG_CO_NGHIA.filter((a) => a !== 'LOGIN'), SO_HOAT_DONG_TT, coNhatKy);   // TK-34 lượt 3: không đưa đăng nhập vào Tổng quan
  const { data: cay } = useTree();
  const [xem, setXem] = useState<'mo' | 'xong'>('mo');
  const [loc, setLoc] = useState<LocMuc>('tat');
  const bayGio = Date.now();

  /* Số trên tab/chip = `useDemSuCo` (cùng nguồn với ô số + chuông); chưa có số đếm ⇒ đếm trên dòng đã tải. Danh sách = dòng đã tải. */
  const soMoTai = dong.filter(LOC_TAB.mo).length;
  const soMo = soSuCoMo(dem, dong);
  const soXong = dong.filter(LOC_TAB.xong).length;
  const demChip = (k: Exclude<LocMuc, 'tat'>) => dem?.[k] ?? dong.filter(LOC_TAB[k]).length;
  const hang = dong.filter(LOC_TAB[xem]).filter((d) => xem === 'xong' || loc === 'tat' || LOC_TAB[loc](d));
  const dinhVi = (id: string) => (onDinhVi ? onDinhVi(id) : navigate(`/?tu=${id}`));
  const tenTu = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of cay ?? []) for (const c of r.cabinets ?? []) m.set(c.id, c.name);
    return m;
  }, [cay]);
  /* Danh sách tải tối đa 100 hồ sơ mở: ít hơn số máy chủ đếm ⇒ nói CẢ HAI số (luật số 0), bằng nhau ⇒ im. */
  const tong = dem?.mo;

  const CHIP: { k: LocMuc; ten: string }[] = [
    { k: 'tat', ten: 'Tất cả' }, { k: 'khan', ten: 'Khẩn cấp' }, { k: 'canh', ten: 'Cảnh báo' }, { k: 'mat', ten: 'Mất kết nối' },
  ];

  return (
    <section className={`flex min-h-0 flex-col bg-nen-0 px-4 py-5 ${className}`} data-o="trung-tam-su-co" id="trung-tam-su-co" tabIndex={-1}>
      <div className="flex items-center justify-between">
        <h2 className="m-0 text-[17px] font-semibold text-chu-1">Trung tâm sự cố</h2>
        <RadioTower className="h-[17px] w-[17px] text-chu-2" aria-hidden="true" />
      </div>
      <div role="tablist" aria-label="Sự cố" className="mb-3 mt-3 flex gap-[22px] border-b border-vien">
        {([['mo', 'Đang mở', soMo], ['xong', 'Đã xử lý', soXong]] as const).map(([k, ten, n]) => (
          <button key={k} type="button" role="tab" aria-selected={xem === k} data-tab={k} onClick={() => setXem(k)}
            className={`-mb-px inline-flex items-center gap-1.5 border-0 border-b-2 bg-transparent px-0 py-2 text-xs ${xem === k ? 'border-nhan text-nhan' : 'border-transparent text-chu-2 hover:text-chu-1'}`}>
            {ten}<span className="rounded-nut bg-nhan/20 px-1.5 py-0.5 text-[11px] tabular-nums text-nhan" data-so-tab>{hai(n)}</span>
          </button>
        ))}
      </div>
      {xem === 'mo' && (
        <div className="mb-3.5 flex flex-wrap gap-1.5" role="group" aria-label="Mức">
          {CHIP.map((c) => (
            <button key={c.k} type="button" aria-pressed={loc === c.k} data-chip={c.k} onClick={() => setLoc(c.k)}
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-1.5 text-[11px] ${loc === c.k ? 'border-nhan/50 bg-nen-3 text-nhan' : 'border-vien text-chu-1 hover:bg-nen-2'}`}>
              {c.ten}{c.k !== 'tat' && <span className="tabular-nums text-chu-2" data-so-chip>{hai(demChip(c.k))}</span>}
            </button>
          ))}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {dangTai && <Spinner label="Đang tải…" />}
        {/* Rỗng: HÌNH + câu (chữ mẫu), màu `nhan` — KHÔNG màu lá: "không có sự cố" không nói hệ đang chạy đúng ([95]). */}
        {!dangTai && hang.length === 0 && (
          <div className="px-2.5 py-8 text-center" data-khong-viec>
            <CheckCircle2 className="mx-auto mb-1.5 h-6 w-6 text-nhan" aria-hidden="true" />
            <p className="m-0 text-xs text-chu-2">Không có sự cố trong bộ lọc này.</p>
          </div>
        )}
        {hang.map((d) => {
          const a = d.alarm;
          const tieuDe = a && d.nguon === 'tu' ? tenViec(a.code) : d.tieu;
          const noi = d.nguon === 'nema' ? d.noi : (a?.cabinet?.name ?? (d.cabinetId ? tenTu.get(d.cabinetId) : null) ?? 'Tủ');
          const Icon = d.nguon === 'nema' ? Lightbulb : d.muc === 'mat' ? WifiOff : d.muc === 'khan' ? AlertOctagon : AlertTriangle;
          const mauIcon = d.muc === 'khan' ? 'text-nguy' : d.muc === 'canh' ? 'text-canh' : d.muc === 'xong' ? 'text-tot' : 'text-chu-3';
          const tt = d.muc === 'xong' ? 'Đã xử lý' : a?.resolvedAt ? 'Chờ tủ xác nhận' : d.daNhan ? 'Đang xử lý' : 'Chưa tiếp nhận';
          return (
            <article key={d.id} className={`mb-2 border-b border-l-[3px] border-b-vien bg-nen-1 py-[15px] pl-3.5 pr-2.5 ${VACH[d.muc]}`}
              data-dong={d.id} data-muc={d.muc} data-nguon={d.nguon}>
              <h3 className="m-0 mb-2 flex items-center gap-2 text-xs font-semibold text-chu-1">
                <Icon className={`h-[15px] w-[15px] shrink-0 ${mauIcon}`} aria-hidden="true" />
                <span className="min-w-0 truncate">{tieuDe}</span>
                {d.giaLap && <Badge tone="slate">GIẢ LẬP</Badge>}
              </h3>
              <p className="m-0 my-2 truncate text-[11px] text-chu-2">{noi}{d.luc ? ` · ${tuoi(d.luc, bayGio)}` : ''}</p>
              <span className={`inline-block rounded-full border px-[7px] py-[3px] text-[10px] ${tt === 'Đã xử lý' ? 'border-tot/40 text-tot' : tt === 'Chờ tủ xác nhận' ? 'border-vien-manh text-chu-2' : 'border-canh/45 text-canh'}`} data-trang-thai>{tt}</span>
              <div className="mt-3 flex gap-[7px]">
                {d.cabinetId && (
                  <button type="button" onClick={() => dinhVi(d.cabinetId!)} data-o="dinh-vi"
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-nut border border-vien px-3 py-2 text-[11px] text-chu-1 hover:bg-nen-2">
                    <MapPin className="h-[15px] w-[15px]" aria-hidden="true" />Định vị
                  </button>
                )}
                {a && d.muc !== 'xong' && !a.resolvedAt && !d.giaLap && (
                  <div className="flex flex-1">
                    <CoQuyen khoa="nhan_canh_bao" hienHuyHieu={false}>
                      <button type="button" disabled={ban} data-o={d.daNhan ? 'hoan-tat' : 'tiep-nhan'}
                        onClick={() => (d.daNhan ? xuLy.mutate(a.id) : ghiNhan.mutate(a.id))}
                        className="inline-flex w-full items-center justify-center rounded-nut border border-nhan-dam bg-nhan-dam px-3 py-2 text-[11px] text-tren-nhan hover:bg-nhan disabled:opacity-60">
                        {d.daNhan ? 'Hoàn tất' : 'Tiếp nhận'}
                      </button>
                    </CoQuyen>
                  </div>
                )}
              </div>
            </article>
          );
        })}
        {xem === 'mo' && tong != null && !dangTai && soMoTai !== tong && (
          <p className="m-0 px-1 py-2 text-[11px] text-chu-3" data-o="chan-danh-sach">Đang mở · <b className="text-chu-2">{soMoTai}</b> / {tong} hồ sơ đã tải</p>
        )}

        <div className="mt-5 border-t border-vien pt-5" data-o="hoat-dong-gan-day">
          <div className="flex items-center">
            <h3 className="m-0 text-[13px] font-semibold text-chu-1">Hoạt động gần đây</h3>
            {coNhatKy && <Link to="/nhat-ky" className="ml-auto text-[11px] text-nhan hover:underline">Xem tất cả</Link>}
          </div>
          {!coNhatKy ? <div className="mt-3">{daBietQuyen ? <HuyHieuKhoa ten="Nhật ký" /> : <DauDangKiem />}</div>
            : hd.isLoading ? <Spinner label="Đang tải…" />
              : (hd.data ?? []).length === 0 ? <p className="m-0 py-3 text-[11px] text-chu-3">Chưa có thao tác</p>
                : [...(hd.data ?? [])].filter((r) => HOAT_DONG_CO_NGHIA.includes(r.action))
                  .sort((x, y) => new Date(y.at).getTime() - new Date(x.at).getTime()).slice(0, SO_HOAT_DONG_TT).map((r) => {
                    const idTu = r.target?.startsWith('cabinet:') ? r.target.slice(8) : null;
                    const idTuAlarm = !idTu && typeof (r.detail as { cabinetId?: unknown } | null)?.cabinetId === 'string' ? (r.detail as { cabinetId: string }).cabinetId : null;
                    const thietBi = r.target?.startsWith('thiet-bi:') ? r.target.slice(9) : null;
                    const cau = dongHoatDong(r.action, r.detail, (idTu ?? idTuAlarm) ? (tenTu.get((idTu ?? idTuAlarm)!) ?? null) : null);
                    const ai = r.user ? (r.user.fullName || r.user.username) : (thietBi ? `Thiết bị ${thietBi}` : aiDaLam(r.action, false));
                    const g = new Date(r.at);
                    return (
                      <div key={r.id} className="flex gap-[11px] py-3 text-[11px]" data-hoat-dong-muc={r.id}>
                        <time className="w-[50px] shrink-0 tabular-nums text-chu-2">{`${hai(g.getHours())}:${hai(g.getMinutes())}:${hai(g.getSeconds())}`}</time>
                        <span className={`mt-[5px] inline-block h-[7px] w-[7px] shrink-0 rounded-full ${LOP_HD[cau.loai]}`} />
                        <p className="m-0 min-w-0 leading-relaxed text-chu-1">{cau.cau}<small className="block text-chu-2">{ai}</small></p>
                      </div>
                    );
                  })}
        </div>
      </div>
    </section>
  );
}
